import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { requireActualVsBudgetMockAccess } from '@/features/actual-vs-budget/access'
import { actualVsBudgetExperimentViewModel } from '@/features/actual-vs-budget/fixtures'
import { PortfolioMatrix } from '@/components/custom/actual-vs-budget/portfolio-matrix'
import {
  createDefaultPortfolioMatrixState,
  type PortfolioMatrixState,
} from '@/features/actual-vs-budget/portfolio-matrix-state'
import { findCompanyReview } from '@/features/actual-vs-budget/company-review'
import { CompanyReviewView } from '@/components/custom/actual-vs-budget/company-review-view'

interface ActualVsBudgetSearch {
  company?: string
}

export const Route = createFileRoute(
  '/_authenticated/experiments/actual-vs-budget'
)({
  beforeLoad: requireActualVsBudgetMockAccess,
  validateSearch: (search: Record<string, unknown>): ActualVsBudgetSearch => ({
    company:
      typeof search.company === 'string' && search.company.trim().length > 0
        ? search.company
        : undefined,
  }),
  component: ActualVsBudgetExperimentPage,
})

function ActualVsBudgetExperimentPage() {
  const { company: companyId } = Route.useSearch()
  const [portfolioState, setPortfolioState] = useState<PortfolioMatrixState>(
    createDefaultPortfolioMatrixState
  )
  const { badgeLabel, companies, disclosure } =
    actualVsBudgetExperimentViewModel
  const companyReview = companyId
    ? findCompanyReview(companies, companyId)
    : undefined

  // Scope a more restrained unfavorable-variance red to this experiment only.
  // Both tables and the matrix legend read `text-destructive`/`bg-destructive`,
  // so overriding `--destructive` here softens every unfavorable figure in one
  // place (keeping the two views unified) without touching the global token that
  // the rest of the app uses for destructive buttons and error states. The
  // default #df0016 is the brightest color in the feature and reads as an alarm;
  // a deeper brick red stays legible (higher contrast on white) and on-palette.
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-y-auto pb-8 pr-4 md:pr-8 [--destructive:#b3261e] dark:[--destructive:#ef8577]">
      <PageHeader title="Actuals vs Budget" useSeparator={false}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="badge"
                size="badge"
                aria-label={`${badgeLabel}: ${disclosure}`}
              >
                {badgeLabel}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs leading-relaxed">
              {disclosure}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </PageHeader>

      {companyReview ? (
        <CompanyReviewView review={companyReview} />
      ) : companyId ? (
        <div className="rounded-lg border border-primary/10 bg-white p-8 text-center">
          <Typography variant="h5">Company review unavailable</Typography>
          <Typography variant="muted" className="mt-2">
            This company is not part of the current experiment.
          </Typography>
          <Button asChild variant="outline" className="mt-5">
            <Link
              to="/experiments/actual-vs-budget"
              search={{ company: undefined }}
            >
              Back to portfolio
            </Link>
          </Button>
        </div>
      ) : (
        <PortfolioMatrix
          companies={companies}
          state={portfolioState}
          onStateChange={setPortfolioState}
        />
      )}

      <Typography className="border-l-2 border-secondary/50 pl-3 text-sm leading-6 text-muted-foreground">
        {disclosure}
      </Typography>
    </div>
  )
}
